/**
 * [Insider+] Outer-portal / Discourse entry detection + landing-page seeding.
 */

import TagManager from 'react-gtm-module';
import history from '../../store/utils/history';
import { INSIDER_PLUS_PAGE } from '../../constants/pages';
import {
  INSIDER_PLUS_ENTRY_SOURCE,
  LANDING_PAGE,
} from '../../constants/sessionStorage';
import {
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from './sessionStorageHelper';
import {
  getLegacyInsiderPlusPath,
  isSafeInsiderPlusPath,
} from './insiderPlusPathHelper';

const INSIDER_PLUS_CLICKED_PARAM = 'InsiderPlusClicked';
const INSIDER_PLUS_PATH_PARAM = 'InsiderPlusPath';
const INSIDER_DEEP_LINK_PREFIX = '/insider/';
// Child-app contract: /join?openCheckout=true opens its checkout modal on
// load. The marketing "Join" CTA below is the one entry point that should
// jump straight into checkout rather than just landing on the join page.
export const OPEN_CHECKOUT_PARAM = 'openCheckout';
export const OUTER_PORTAL_SOURCE = 'outer_portal';
export const DISCOURSE_SOURCE = 'discourse';

// Reset the once-per-journey funnel flags (see pushInsiderFunnelEventOnce).
// Called at the start of a new Insider+ entry so a repeat journey in the same
// tab (e.g. after logout) re-emits its terminal events.
export const clearInsiderFunnelFlags = () => {
  if (typeof window === 'undefined') return;
  Object.keys(window.sessionStorage)
    .filter((k) => k.startsWith('insider_funnel_fired_'))
    .forEach((k) => window.sessionStorage.removeItem(k));
};

// Rewrites a pre-rebrand URL (/insider-plus[/...], /insider-plus-membership)
// to its Investor Hub path, keeping the query string and hash. Must run before
// the two entry handlers below: they only recognise the current path, so an old
// /insider-plus?InsiderPlusClicked=yes link would otherwise not count as a
// fresh entry and lose its ?openCheckout=true landing. The router has its own
// redirect for in-app navigation to the old paths.
export const normalizeLegacyInsiderPlusUrl = () => {
  if (typeof window === 'undefined') return null;

  const { pathname, search, hash } = window.location;
  const next = getLegacyInsiderPlusPath(pathname);
  if (!next) return null;

  history.replace(next + search + hash);
  return next;
};

// Detects the ?InsiderPlusClicked=yes deep-link from the marketing site
// (goldsilver.com) and seeds the existing post-login landing-page flow so
// the user ends up on /investor-hub after login + 2FA. Mirrors the BUY
// default in landingPageHelper.
export const handleInsiderPlusEntry = () => {
  if (typeof window === 'undefined') return null;

  const url = new URL(window.location.href);
  const flag = url.searchParams.get(INSIDER_PLUS_CLICKED_PARAM);
  if (flag !== 'yes') return null;

  clearInsiderFunnelFlags();
  storeInSessionStorage(
    LANDING_PAGE,
    `${INSIDER_PLUS_PAGE}?${OPEN_CHECKOUT_PARAM}=true`,
  );
  storeInSessionStorage(INSIDER_PLUS_ENTRY_SOURCE, OUTER_PORTAL_SOURCE);

  // ?InsiderPlusClicked=yes is dual-purpose: a fresh marketing-site CTA
  // (pathname is the bare root, or already /investor-hub) that should land
  // on /investor-hub right now, vs. withInsiderPlusOutboundContext()
  // re-tagging this same param onto an in-app link (e.g. the "Sign up" CTA
  // on the login page, which points at /registration) purely so the entry
  // context survives that detour. Only the former should redirect — for
  // the latter, LANDING_PAGE above already guarantees /investor-hub?
  // openCheckout=true once login/registration completes, so clobbering
  // /registration into /investor-hub here would just yank the visitor away
  // from the page they were actually headed to (and, since they're
  // unauthenticated there, straight back to the login page).
  const isFreshEntry =
    url.pathname === '/' ||
    url.pathname === INSIDER_PLUS_PAGE ||
    url.pathname.startsWith(`${INSIDER_PLUS_PAGE}/`);
  url.searchParams.delete(INSIDER_PLUS_CLICKED_PARAM);
  if (isFreshEntry) {
    // Use the app's own history singleton (not window.history.replaceState
    // directly): <Router history={history}> in App.js means every
    // useLocation()/useHistory() in the app — including InsiderPlusPage's
    // buildIframeSrc — reads from history's own cached `.location`, which is
    // only updated via its push()/replace()/a popstate event. A raw
    // replaceState() call updates the address bar but never notifies
    // history's listeners, so react-router (and the iframe src it drives)
    // silently keeps seeing the pre-rewrite URL forever.
    url.pathname = INSIDER_PLUS_PAGE;
    url.searchParams.set(OPEN_CHECKOUT_PARAM, 'true');
  }
  history.replace(url.pathname + url.search + url.hash);

  TagManager.dataLayer({
    dataLayer: {
      event: 'investor_hub_entry',
      source: OUTER_PORTAL_SOURCE,
    },
  });

  return OUTER_PORTAL_SOURCE;
};

// Insider+ entry context for the login/registration funnel. `source` is the
// seeded entry source (outer_portal / discourse) or null; `insider_plus` marks
// whether the user is in the Insider+ journey.
export const getInsiderPlusContext = () => {
  const source = retrieveFromSessionStorage(INSIDER_PLUS_ENTRY_SOURCE) || null;
  return {
    insider_plus: source === OUTER_PORTAL_SOURCE || source === DISCOURSE_SOURCE,
    source,
  };
};

// Push a login/registration funnel event for Insider+ journeys ONLY. No-ops on
// the non-Insider flow (and when GTM isn't loaded). Safe to call anywhere
// (component or saga); callers don't need to check the entry source themselves.
export const pushInsiderFunnelEvent = (event, extra = {}) => {
  if (typeof window === 'undefined') return;
  const context = getInsiderPlusContext();
  // Insider+ journeys only — never emit on the non-Insider flow.
  if (!context.insider_plus) return;
  // Only `source` is carried: since these events fire on Insider+ journeys
  // exclusively, an insider_plus flag would always be true (redundant).
  TagManager.dataLayer({
    dataLayer: { event, source: context.source, ...extra },
  });
};

// Like pushInsiderFunnelEvent, but fires at most once per session per event.
// For terminal/entry events that can otherwise re-fire across iframe-page mounts
// or SSO round-trips (e.g. investor_hub_opened).
export const pushInsiderFunnelEventOnce = (event, extra = {}) => {
  if (typeof window === 'undefined') return;
  if (!getInsiderPlusContext().insider_plus) return;
  const key = `insider_funnel_fired_${event}`;
  if (retrieveFromSessionStorage(key)) return;
  storeInSessionStorage(key, true);
  pushInsiderFunnelEvent(event, extra);
};

// The Discourse (/forum-auth) entry — mirrors handleInsiderPlusEntry's
// outer_portal `investor_hub_entry` push so both entry sources are tracked.
export const trackDiscourseInsiderEntry = () => {
  if (typeof window === 'undefined') return;
  clearInsiderFunnelFlags();
  TagManager.dataLayer({
    dataLayer: { event: 'investor_hub_entry', source: DISCOURSE_SOURCE },
  });
};

// Detects an inbound external deep-link into a specific Insider+ sub-page —
// either the /insider/<path> splat (e.g. shared/marketing short links) or an
// ?InsiderPlusPath=<path> query param on any URL — and rewrites the URL to
// the real, visible /investor-hub/<path> so the address bar shows exactly
// what's loaded (InsiderPlusPage derives the iframe src straight from that
// URL — no hidden storage involved). LANDING_PAGE is seeded with the same
// full path so it survives login/2FA via the existing generic mechanism.
export const handleInsiderPlusDeepLink = () => {
  if (typeof window === 'undefined') return null;

  const url = new URL(window.location.href);

  const isSplatEntry = url.pathname.startsWith(INSIDER_DEEP_LINK_PREFIX);
  let deepPath = null;
  if (isSplatEntry) {
    deepPath = `/${url.pathname.slice(INSIDER_DEEP_LINK_PREFIX.length)}`;
  } else {
    deepPath = url.searchParams.get(INSIDER_PLUS_PATH_PARAM);
  }

  // Path only — a query string here would get percent-encoded as literal
  // path content by the url.pathname assignment below (it doesn't parse `?`
  // as a separator the way history.push/replace does), corrupting the
  // visible URL. Deep links don't need to carry their own query string.
  if (typeof deepPath === 'string') {
    [deepPath] = deepPath.split('?');
  }

  if (!isSafeInsiderPlusPath(deepPath)) return null;

  clearInsiderFunnelFlags();
  const targetPath = `${INSIDER_PLUS_PAGE}${deepPath}`;

  storeInSessionStorage(LANDING_PAGE, targetPath);
  storeInSessionStorage(INSIDER_PLUS_ENTRY_SOURCE, OUTER_PORTAL_SOURCE);

  // ?InsiderPlusPath=<path> is dual-purpose the same way ?InsiderPlusClicked
  // is (see handleInsiderPlusEntry): a fresh deep-link entry — either the
  // /insider/ splat form, or the query param arriving on a generic/
  // insider-plus pathname — that should redirect into the module now, vs.
  // withInsiderPlusOutboundContext() re-tagging the query-param form onto
  // an unrelated in-app link (e.g. the "Sign up" CTA -> /registration)
  // purely to preserve entry context through that detour, which must not
  // redirect away from wherever the visitor was actually headed. The
  // /insider/ splat form is never produced by withInsiderPlusOutboundContext,
  // so it's always a genuine fresh entry.
  //
  // See the matching note in handleInsiderPlusEntry: go through the app's
  // history singleton, not window.history.replaceState directly, so
  // react-router's cached location actually picks up this rewrite.
  const isFreshEntry =
    isSplatEntry ||
    url.pathname === '/' ||
    url.pathname === INSIDER_PLUS_PAGE ||
    url.pathname.startsWith(`${INSIDER_PLUS_PAGE}/`);
  url.searchParams.delete(INSIDER_PLUS_PATH_PARAM);
  if (isFreshEntry) {
    url.pathname = targetPath;
  }
  history.replace(url.pathname + url.search + url.hash);

  // No GTM event here (unlike handleInsiderPlusEntry above): the deep child
  // route already fires its own page_view once the iframe mounts, so an
  // entry-level event would just double up funnel data.

  return targetPath;
};

// sessionStorage doesn't cross origins. A visitor who arrives via an
// Insider+ link and then clicks "Sign up" (SignUpUrl, or a broker's
// PortalUrl-hosted registration) may leave this origin entirely — losing
// LANDING_PAGE before they ever create an account. Re-encoding the current
// entry as a query param on that outbound link lets it survive the hop IF
// the destination eventually links back here with the param intact — same
// contract as the inbound handlers above, just mirrored outbound. Returns
// null outside an Insider+ journey (nothing to carry).
export const getInsiderPlusOutboundParams = () => {
  if (typeof window === 'undefined') return null;
  if (
    retrieveFromSessionStorage(INSIDER_PLUS_ENTRY_SOURCE) !==
    OUTER_PORTAL_SOURCE
  ) {
    return null;
  }

  const landingPage = retrieveFromSessionStorage(LANDING_PAGE);
  // PrivateRoute stores LANDING_PAGE as pathname + search generically, so an
  // unrelated query string (UTM params, etc.) can ride along after the deep
  // path — strip it. See the matching note in handleInsiderPlusDeepLink for
  // why a query string can't survive the round trip through url.pathname.
  const deepPath =
    typeof landingPage === 'string' &&
    landingPage.startsWith(`${INSIDER_PLUS_PAGE}/`)
      ? landingPage.slice(INSIDER_PLUS_PAGE.length).split('?')[0]
      : null;

  return deepPath
    ? { [INSIDER_PLUS_PATH_PARAM]: deepPath }
    : { [INSIDER_PLUS_CLICKED_PARAM]: 'yes' };
};

// Appends getInsiderPlusOutboundParams() onto `url`, preserving any params
// it already has. Returns `url` unchanged if there's nothing to carry or it
// isn't a parseable URL (e.g. an empty/misconfigured broker setting).
export const withInsiderPlusOutboundContext = (url) => {
  if (!url || typeof window === 'undefined') return url;
  const params = getInsiderPlusOutboundParams();
  if (!params) return url;

  try {
    const target = new URL(url, window.location.origin);
    Object.entries(params).forEach(([key, value]) => {
      target.searchParams.set(key, value);
    });
    return target.toString();
  } catch {
    return url;
  }
};
